// Comprehensive types for spreadsheet table functionality

import React from "react";
import {
  SmartColumn,
  CellData,
} from "@modules/spreadsheets/dto/spreadsheet/type";
import { DeletedRowData } from "./utils/undo-redo.types";
import { MessageShowParams } from "@common/hooks/message";

// =============================================================================
// CORE DATA TYPES
// =============================================================================

export type SpreadsheetTableDefaultDataItem = {
  id: string;
  rowCount?: number;
  srNo?: number;
  globalId?: number;
  fieldContext?: number;
  rrn?: number;
} & Record<string, any>;

// =============================================================================
// COLUMN TYPES
// =============================================================================

export enum SpreadsheetTableColumnType {
  text = "text",
  static = "static",
}

export type SpreadsheetTableColumnRenderCellParams<
  DataItem = SpreadsheetTableDefaultDataItem
> = {
  dataItem: DataItem;
};

export type SpreadsheetTableColumn<DataItem = SpreadsheetTableDefaultDataItem> =
  {
    id: number;
    header?: React.ReactNode;
    type: SpreadsheetTableColumnType | string;
    accessorKey?: string;
    colName: string;
    cell?: (
      params: SpreadsheetTableColumnRenderCellParams<DataItem>
    ) => React.ReactNode;
    enableResizing?: boolean;
    maxSize?: number;
    minSize?: number;
    size?: number;
    filter?: SpreadsheetTableColumnFilter;
    fieldClass?: string;
    system?: boolean;
    tableIndex?: number;
    editable: boolean;
    securityEditable?: boolean;
    required?: boolean;
    typeId?: number;
    sortOrder?: number;
    sortAsc?: boolean;
    dropdownOptions?: Array<SpreadsheetTableColumnFilterOption>;
    dataDropdownOptions?: Array<SpreadsheetTableColumnFilterOption>;
    modulo?: number;
    parentFieldName?: string;
    parentField?: SpreadsheetTableParentField;
    fieldType?: string;
    columnOrder?: number;
    width?: 100;
    visible?: boolean;
    lookupValues?: Record<string, string[]> | {};
    field?: {
      fieldid: number;
      fieldname: string;
      system: boolean;
      fieldcontext: string;
      type: number;
      fieldnameWithPath?: string;
    };
  };

// =============================================================================
// FILTER TYPES
// =============================================================================

export enum SpreadsheetTableFilterType {
  text = 1,
  date = 2,
  number = 3,
  checkbox = 4,
  dropdown = 5,
  image = 6,
  lookupTable = 7,
  table = 8,
  computed = 9,
  summary = 11,
  tableSummary = 13,
  hyperlink = 14,
  integer = 30,
  folder = 31,
}

export type SpreadsheetTableColumnFilterOption = {
  actualValue: string | number;
  displayValue: string;
};

export type SpreadsheetTableColumnFilter = {
  type: SpreadsheetTableFilterType;
  options?: Array<SpreadsheetTableColumnFilterOption>;
  outputDateFormat?: string;
  getFolders?: () => Promise<Array<any>>;
  lookupValues?: Record<string, string[]> | {};
};

export type SpreadsheetTableColumnId = SpreadsheetTableColumn["id"];
export type SpreadsheetTableColumnName = SpreadsheetTableColumn["colName"];

// Selection types for copy functionality
export type SpreadsheetTableCellSelection = {
  rowIndex: number;
  columnId: SpreadsheetTableColumnId;
};

export type SpreadsheetTableSelect = {
  start: SpreadsheetTableCellSelection;
  end: SpreadsheetTableCellSelection;
};

export type SpreadsheetTableSelectionState = {
  selection: SpreadsheetTableSelect | null;
  isSelecting: boolean;
};

// Clipboard types
export type SpreadsheetTableClipboardData = {
  text: string;
  html?: string;
};

export enum SpreadsheetTableFilterApplyType {
  and = "0",
  or = "1",
}

export type SpreadsheetTableFilterValue = {
  operator1?: number | string;
  whereClause1?: number | string;
  andOR?: SpreadsheetTableFilterApplyType;
  operator2?: number | string;
  whereClause2?: number | string;
};

export type SpreadsheetTableLoadMetadataFiltering = {
  filter: boolean;
  columns: Record<SpreadsheetTableColumnId, SpreadsheetTableFilterValue>;
};

export type SpreadsheetTableLoadMetadataPagination = {
  start: number;
  size: number;
};

export type SpreadsheetTableLoadMetadata = {
  filtering?: SpreadsheetTableLoadMetadataFiltering;
  pagination: SpreadsheetTableLoadMetadataPagination;
};

export type SpreadsheetTableFilterId = number | string;
export type SpreadsheetTableFilterName = string;

export type SpreadsheetTableFilters = {
  operators: Record<
    SpreadsheetTableFilterId,
    {
      name: SpreadsheetTableFilterName;
      isValidValue?: (value: any) => boolean;
    }
  >;
};

// =============================================================================
// ACTION TYPES
// =============================================================================

export enum SpreadsheetTableColumnPredefinedActionType {
  filters = "filters",
  deleteColumn = "deleteColumn",
  addColumn = "addColumn",
  clearSorting = "clearSorting",
}

export type SpreadsheetTableColumnAction = {
  className?: string;
  label?: string;
  type?: SpreadsheetTableColumnPredefinedActionType;
  onClick?: (params?: { columnId?: SpreadsheetTableColumnId }) => void;
};

export type SpreadsheetTableHeaderAction = {
  className?: string;
  label?: string;
  onClick?: () => void;
};

export type SpreadsheetTableRowAction = {
  label?: string;
  onClick?: () => void;
};

// =============================================================================
// SELECTION TYPES
// =============================================================================

export type SpreadsheetTableSelectionType = "multiple" | "single";

export type SpreadsheetTableSelection<
  DataItem = SpreadsheetTableDefaultDataItem
> = {
  type: SpreadsheetTableSelectionType;
  items: Array<DataItem>;
  onChange: (items: Array<DataItem>) => void;
};

export type SpreadsheetTableColumnsResizing = {
  onChange?: (
    columns: Record<SpreadsheetTableColumnId, number>
  ) => void | Promise<void>;
};

// =============================================================================
// MAIN PROPS TYPES
// =============================================================================

export type SpreadsheetTableProps<DataItem = SpreadsheetTableDefaultDataItem> = {
  className?: string;
  getColumns: () => Promise<Array<SpreadsheetTableColumn<DataItem>>>;
  onLoadData: (loadMetadata: SpreadsheetTableLoadMetadata) => Promise<void>;
  spreadsheetId: number;
  spreadsheetType: string;
  runPayload?: SpreadsheetTableRunPayload;
  folderId?: number;
  familyId?: number;
  isTemporaryRun?: boolean;
  showInfoMessage?: (message: string) => void;
  showMessage?: (config: MessageShowParams) => void;
  showErrorMessage?: (message: string) => void;
  onSelectedRowDataChange?: (
    selectedRowData: SpreadsheetTableDefaultDataItem[]
  ) => void;
  onCurrentDataChange?: (data: SpreadsheetTableDefaultDataItem[]) => void;
  onSmartColumnsChange?: (smartColumns: SmartColumn[]) => void;
  onSelectedColumnIndexChange?: (selectedColumnIndex: number | null) => void;
  onDataResultChange?: (dataResult: SpreadsheetTableApiResponse) => void;
  onTotalCountChange?: (totalCount: number | null) => void;
  onHasRrnChange?: (hasRrn: boolean) => void;
  onHasParentFieldChange?: (hasParentField: boolean) => void;
  onCanAddRowChange?: (canAddRow: boolean) => void;
  onCanDeleteRowChange?: (canDeleteRow: boolean) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onDataChange?: () => Promise<void>;
  onAddRow?: () => void;
  onDeleteRow?: () => void;
  onClearSelection?: () => void;
  isAddingRow?: boolean;
  // Undo/Redo functions from parent
  trackCellEdit?: (
    currentPayload: any,
    rowIndex: number,
    columnId: any,
    previousPayload: any,
    popPreviousUndo?: boolean
  ) => void;
  trackRowAdd?: (
    rowIndex: number,
    columnId: any,
    globalId: number,
    tableId: number,
    context: number
  ) => void;
  trackRowDelete?: (
    deletedRows: Array<{
      rowIndex: number;
      columnId: SpreadsheetTableColumnId;
      globalId: number;
      tableId: number;
      rrn: number;
      deletedRowData: Array<DeletedRowData>;
    }>
  ) => void;
  trackBulkFill?: (
    fillOperations: Array<{
      rowIndex: number;
      columnId: any;
      newValue: string | number;
      originalValue: string | number;
      payload: any;
      columnIndex: number;
    }>,
    fillDirection: "up" | "down"
  ) => void;
  onBulkCellUpdate?: (
    updates: Array<{
      rowIndex: number;
      columnId: any;
      value: string | number;
      payload: any;
      columnIndex: number;
    }>
  ) => Promise<void>;
  parentData?: SpreadsheetTableDefaultDataItem[]; // Data from parent CONTROLLER
  // Undo/Redo functions and state
  undo?: () => void;
  redo?: () => void;
  canUndo?: boolean;
  // Callback to expose table controller refs
  onControllerRefsReady?: (refs: {
    queryDetailsRef: React.RefObject<any>;
    activeFilterRef: React.RefObject<any>;
    formatDetailsRef: React.RefObject<any>;
  }) => void;
  canRedo?: boolean;
  // Undo/Redo callbacks
  onUndoRedoStateChange?: (state: {
    canUndo: boolean;
    canRedo: boolean;
    onUndo: () => void;
    onRedo: () => void;
  }) => void;
  clearSelectionEvent: boolean;
  onSetClearSelectionEvent: (clearSelectionEvent: boolean) => void;
  isProcessingUndoRedo: boolean;
  isDeletingRow: boolean;
};

// =============================================================================
// RUN PAYLOAD TYPES
// =============================================================================

export type SpreadsheetTableRunPayload = {
  columnModel: {
    smartColumns: SmartColumn[];
  };
  ssFormat?: SpreadsheetTableFormatDetails;
  queryModel?: SpreadsheetTableQueryModel;
  folderId?: number;
  familyId?: number;
};

export type SpreadsheetTableFormatDetails = {
  id?: number;
  name?: string;
  folderId?: number;
  description?: string;
};

export type SpreadsheetTableQueryModel = {
  whereClauses: SpreadsheetTableWhereClause[];
  andOR?: number;
};

export type SpreadsheetTableWhereClause = {
  field: SpreadsheetTableField;
  operator: number;
  value: string;
  fieldClass?: string;
  parentField?: SpreadsheetTableParentField;
  andOr?: string;
  andOR?: string;
  leftParenthesis?: boolean;
  rightParenthesis?: boolean;
};

export type SpreadsheetTableField = {
  fieldid: number;
  type: number;
  fieldcontext: string;
  system: boolean;
};

export type SpreadsheetTableParentField = {
  fieldid: number;
  fieldname?: string;
  fieldcontext?: string;
  type?: number;
  system?: boolean;
};

// =============================================================================
// API PAYLOAD TYPES
// =============================================================================

export interface SpreadsheetTableLoadISSPayload {
  folderId: number;
  familyId: number;
  startAt: number;
  size: number;
  scroll: boolean;
  columnModel: SpreadsheetTableColumnModel;
  queryModel: SpreadsheetTableQueryModel;
  filter: SpreadsheetTableFilterModel;
}

export interface SpreadsheetTableColumnModel {
  smartColumns: SpreadsheetTableColumn[];
}

export interface SpreadsheetTableSmartColumn {
  width: number;
  field: SpreadsheetTableApiField;
  type: number;
  id: number;
  order?: number;
  columnOrder?: number;
  visible?: boolean;
  fieldClass?: string;
  tableIndex?: number;
  sortOrder?: number;
  sortAsc?: boolean;
  editable?: boolean;
  dropdownValues?: string[];
  parentField?: SpreadsheetTableApiParentField;
  modulo?: number;
}

export interface SpreadsheetTableApiField {
  fieldid: number;
  fieldname: string;
  fieldtype: SpreadsheetTableFieldType;
  system: boolean;
  systemField: boolean;
  tableIndex: number;
  type: number;
  fieldnameWithPath: string;
  dataSourceNodeId: number;
  dropdownValues?: string[];
  fieldClass?: string;
}

export interface SpreadsheetTableApiParentField {
  fieldid: number;
  fieldname?: string;
  fieldtype?: SpreadsheetTableFieldType;
  system?: boolean;
  dropdownValues?: string[];
}

export interface SpreadsheetTableFieldType {
  typeid: number;
  typename: string;
}

export interface SpreadsheetTableFilterModel {
  whereClauses: SpreadsheetTableWhereClause[];
}

// =============================================================================
// CONTROLLER TYPES
// =============================================================================

export type SpreadsheetTableControllerReturn = {
  setColumnsWidth: React.Dispatch<
    React.SetStateAction<Record<SpreadsheetTableColumnId, number>>
  >;
  columnsWidth: Record<SpreadsheetTableColumnId, number>;
  handleLoadData: (metadata: SpreadsheetTableLoadMetadata) => Promise<void>;
  data: Array<SpreadsheetTableDefaultDataItem>;
  columns: Array<SpreadsheetTableColumn>;
  pageSize: number;
  handleLoadDataWithFilter: (filterPayload: {
    filter: SpreadsheetTableQueryModel;
  }) => Promise<void>;
  handleColumnWidthChange: (
    columnId: SpreadsheetTableColumnId,
    newWidth: number,
    spreadsheetType: string
  ) => Promise<void>;
  handleClearColumnFilter: (columnId: number) => Promise<void>;
};

// =============================================================================
// HEADER COMPONENT TYPES
// =============================================================================

export type SpreadsheetTableHeaderProps = {
  className?: string;
  columns: Array<SpreadsheetTableColumn>;
  columnsWidth: Record<SpreadsheetTableColumnId, number>;
  onColumnsResize: (
    columnId: SpreadsheetTableColumnId,
    newWidth: number,
    spreadsheetType: string
  ) => void;
  onFilter?: (columnId: number, columnName: string) => void;
  currentFilters?: Record<string, any>;
  onClearColumnFilter?: (columnId: number) => void;
  spreadsheetType: string;
};

export type SpreadsheetTableHeaderPopupState = {
  show: boolean;
  x: number;
  y: number;
  columnId: number | null;
};

// =============================================================================
// HEADER CELL TYPES
// =============================================================================

export type SpreadsheetTableHeaderCellProps = {
  currentColumnWidth: number;
  className?: string;
  column: SpreadsheetTableColumn;
  loading: boolean;
  loadTableMetadata?: SpreadsheetTableLoadMetadata;
  onColumnResizeEnd: (
    columnId: SpreadsheetTableColumnId,
    newColumnWidth: number,
    colName: string
  ) => void;
  isFiltered?: boolean;
  onContextMenu?: (
    e: React.MouseEvent<HTMLTableCellElement>,
    columnId: SpreadsheetTableColumnId
  ) => void;
};

export type SpreadsheetTableHeaderCellControllerReturn = {
  resizerOffset: number;
  resizingColumn: boolean;
  handleMouseDown: (event: React.MouseEvent<HTMLSpanElement>) => void;
  handleContextMenu?: (e: React.MouseEvent<HTMLTableCellElement>) => void;
};

// =============================================================================
// BODY COMPONENT TYPES
// =============================================================================

export type SpreadsheetTableBodyProps = {
  columnsWidth: Record<SpreadsheetTableColumnId, number>;
  className?: string;
  columns: Array<SpreadsheetTableColumn>;
  data: Array<SpreadsheetTableDefaultDataItem>;
  onLoadData: (metadata: SpreadsheetTableLoadMetadata) => void;
  pageSize: number;
};

export type SpreadsheetTableBodyControllerReturn = {
  getItemKey: (
    index: number,
    data: Array<SpreadsheetTableDefaultDataItem>
  ) => string;
  rootRef: React.RefObject<HTMLDivElement>;
  bodyHeight: number;
};

// =============================================================================
// ROW COMPONENT TYPES
// =============================================================================

export type SpreadsheetTableRowProps = {
  columnsWidth: Record<SpreadsheetTableColumnId, number>;
  style?: React.CSSProperties;
  className?: string;
  columns: Array<SpreadsheetTableColumn>;
  dataItem: SpreadsheetTableDefaultDataItem;
};

// =============================================================================
// CELL COMPONENT TYPES
// =============================================================================

export type SpreadsheetTableCellProps = {
  className?: string;
  dataItem: SpreadsheetTableDefaultDataItem;
  column: SpreadsheetTableColumn;
  columnWidth: number;
  allColumns?: SpreadsheetTableColumn[];
};

// =============================================================================
// FILTER MODAL TYPES
// =============================================================================

export type SpreadsheetTableFilterModalState = {
  isOpen: boolean;
  columnId: number | null;
  columnName: string | null;
};

export type SpreadsheetTableFilterConfig = {
  type?: SpreadsheetTableFilterType;
  options?: Array<SpreadsheetTableColumnFilterOption>;
  lookupValues?: Record<string, string[]> | {};
};

// =============================================================================
// UTILITY TYPES
// =============================================================================

export type SpreadsheetTableColumnWidthRecord = Record<
  SpreadsheetTableColumnId,
  number
>;

export type SpreadsheetTableCurrentFilters = Record<
  string,
  SpreadsheetTableFilterValue
>;

// Row data structure from loadISS response
export type SpreadsheetTableRowData = {
  id: number;
  name: string;
  customData: Record<string, unknown>;
  readAllowed: boolean;
  writeAllowed: boolean;
  lock: Record<string, unknown> | null;
  folderid: number;
  invitesCount: number;
  familyid: number;
  gender: number;
  fatherid: number;
  motherid: number;
  twinflag: number;
  twinstyle: number;
  genderunknown: boolean;
  primarySampleid: number;
  upn: number;
  singlet: boolean;
  deceased: number;
  genderIdentity: number;
  proband: boolean;
  familyName: string | null;
  imageURL: string;
  male: boolean;
  globalid: number;
  twinGroup: number;
  locked: boolean;
  writable: boolean;
  smartListData: Record<string, unknown>;
};

// Additional row metadata
export type SpreadsheetTableRowMetadata = {
  globalid: number;
  rrn: number;
  id: number;
};

// Main loadISS API response structure
export type SpreadsheetTableApiResponse = {
  count: number;
  nextStart: number;
  tableRows: SpreadsheetTableRowData[] | null;
  data: CellData[][];
  rows: Array<SpreadsheetTableRowData | SpreadsheetTableRowMetadata>;
  columnModel?: {
    smartColumns: SmartColumn[];
  };
};

export type SpreadsheetTableColumnResult = {
  columnModel: {
    smartColumns: SmartColumn[];
  };
  formatDetails: SpreadsheetTableFormatDetails;
  queryModel: SpreadsheetTableQueryModel;
};

// =============================================================================
// CONSTANTS
// =============================================================================

export const SPREADSHEET_TABLE_CONSTANTS = {
  // Pagination
  DEFAULT_PAGE_SIZE: 100,
  MIN_COLUMN_WIDTH: 50,
  MAX_COLUMN_WIDTH: 500,
  DEFAULT_COLUMN_WIDTH: 100,

  // Special column IDs
  ROW_COUNT_FIELD_ID: -599,

  // Column types
  COLUMN_TYPES: {
    STATIC: "static",
    TEXT: "text",
  },

  // Field types
  FIELD_TYPES: {
    SYSTEM: -100,
    STRING: 5,
    TABLE: 8,
    IMAGE: 6,
    DATE: 2,
  },

  // Special field names to exclude - use string array for better compatibility
  EXCLUDED_FIELD_NAMES: ["unknownfield", "unknown field"] as string[],

  // Folder path field identifiers
  FOLDER_PATH_FIELD_NAMES: ["Folder Path"],
  FOLDER_PATH_FIELD_IDS: [73],

  // Local storage keys
  LOCAL_STORAGE_KEYS: {
    SPREADSHEET_COUNT: (id: number) => `spreadsheet-count-${id}`,
    UNSAVED_SS_COUNT: "unsavedSSCount",
  },
} as const;

// =============================================================================
// FILTER OPERATORS
// =============================================================================

export const FILTER_OPERATORS = {
  1: { name: "equals", isValidValue: (value: string | number) => value !== "" },
  4: {
    name: "does not equal",
    isValidValue: (value: string | number) => value !== "",
  },
  2: {
    name: "is less than",
    isValidValue: (value: string | number) => value !== "",
  },
  5: {
    name: "is less than or equal to",
    isValidValue: (value: string | number) => value !== "",
  },
  3: {
    name: "is greater than",
    isValidValue: (value: string | number) => value !== "",
  },
  6: {
    name: "is greater than or equal to",
    isValidValue: (value: string | number) => value !== "",
  },
  7: {
    name: "is blank",
    isValidValue: () => true
  },
  8: {
    name: "is not blank",
    isValidValue: () => true
  },
  9: {
    name: "begins with",
    isValidValue: (value: string | number) => value !== "",
  },
  10: {
    name: "does not begin with",
    isValidValue: (value: string | number) => value !== "",
  },
  11: {
    name: "ends with",
    isValidValue: (value: string | number) => value !== "",
  },
  12: {
    name: "does not end with",
    isValidValue: (value: string | number) => value !== "",
  },
  13: {
    name: "contains",
    isValidValue: (value: string | number) => value !== "",
  },
  14: {
    name: "does not contain",
    isValidValue: (value: string | number) => value !== "",
  },
  17: { name: "like", isValidValue: (value: string | number) => value !== "" },
  18: {
    name: "is not like",
    isValidValue: (value: string | number) => value !== "",
  },
} as const;
